import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { PerfectScrollbarConfigInterface } from 'ngx-perfect-scrollbar';
import { Subscription } from 'rxjs';
import { CadastrarMultiFormaPagamentoModalComponent } from 'src/app/modals/multi-forma-pagamento-modal/cadastrar-multi-forma-pagamento-modal/cadastrar-multi-forma-pagamento-modal.component';
import { CondicaoDePagamento } from 'src/app/models/condicao-de-pagamento';
import { TipoVendaFormaPag } from 'src/app/models/enum';
import { FormaDePagamento } from 'src/app/models/forma-de-pagamento';
import { VendaFormaPag } from 'src/app/models/venda-forma-pag';
import { CarregandoService } from 'src/app/services/carregando.service';
import { CondicaoDePagamentoService } from 'src/app/services/condicao-de-pagamento.service';
import { FormaDePagamentoService } from 'src/app/services/forma-de-pagamento.service';
import { NotificacaoService } from 'src/app/services/notificacao.service';
import swal from 'sweetalert2';

@Component({
  selector: 'app-cadastrar-multi-forma-pagamento',
  templateUrl: './cadastrar-multi-forma-pagamento.component.html',
  styleUrls: ['./cadastrar-multi-forma-pagamento.component.scss']
})
export class CadastrarMultiFormaPagamentoComponent implements OnInit, OnDestroy {
  @Input() forma: VendaFormaPag;
  @Input() pCentroDistribuicao = false;
  @Input() idCD;
  @Input() somenteLeitura = false;
  @Input() suporte = false;
  @Input() multi = false;
  @Input() qtdFormas;
  @Input() mostrarCabecalho;
  @Input() repasse = null;
  @Output() excluir = new EventEmitter();
  @Output() atualizar = new EventEmitter();
  @Output() obterValorRestante = new EventEmitter<{ forma: VendaFormaPag; resolve: (valor: number) => void }>();

  valorRestante = 0;

  formasDePagamento: FormaDePagamento[] = [];
  condicoesDePagamento: Array<{ disabled?: boolean } & CondicaoDePagamento> = [];

  /**
   * As listas foram carregadas e valem para a forma: sem elas, a forma mantém a descrição e a taxa que já tem.
   */
  private formasCarregadas = false;
  private condicoesCarregadas = false;

  tiposRateio: any[] = [
    { id: TipoVendaFormaPag.Fixo, nome: 'Valor Final' },
    { id: TipoVendaFormaPag.Variavel, nome: 'Valor Nominal' }
  ];

  public config: PerfectScrollbarConfigInterface = {};

  constructor(
    private formaDePagamentoService: FormaDePagamentoService,
    private condicaoDePagamentoService: CondicaoDePagamentoService,
    private notificacaoService: NotificacaoService,
    private carregandoService: CarregandoService
  ) {}

  ngOnDestroy(): void {}

  async ngOnInit() {
    if (!this.somenteLeitura) {
      this.buscarFormasDePagamento();
      this.valorRestante = await this.getValorRestante();
    } else {
      await this.buscarFormaPagamento();
      await this.buscarCondicaoPagamento();
    }
  }

  private getValorRestante(): Promise<number> {
    return new Promise((resolve) => {
      this.obterValorRestante.emit({ forma: this.forma, resolve });
    });
  }

  async buscarFormaPagamento() {
    try {
      this.carregandoService.mostra('buscarFormaPagamento');
      this.formasDePagamento = [await this.formaDePagamentoService.consultarPorId(this.forma.idFormaPag).toPromise()];
    } catch (erro) {
      this.notificacaoService.erro(erro);
    } finally {
      this.carregandoService.esconde('buscarFormaPagamento');
    }
  }

  async buscarCondicaoPagamento() {
    try {
      this.carregandoService.mostra('buscarCondicaoPagamento');
      this.condicoesDePagamento = [
        await this.condicaoDePagamentoService.consultarPorId(this.forma.idCondPag).toPromise()
      ];
    } catch (erro) {
      this.notificacaoService.erro(erro);
    } finally {
      this.carregandoService.esconde('buscarCondicaoPagamento');
    }
  }

  buscarFormasDePagamento(sincronizar = false) {
    this.carregandoService.mostra('buscaFormasDePagamento');
    this.formaDePagamentoService.consultar(1, null, this.repasse).subscribe(
      async (forms) => {
        this.formasDePagamento = forms;

        if (this.pCentroDistribuicao) {
          this.formasDePagamento = forms.filter(
            (forma) => forma.fornecedores.length === 0 || forma.fornecedores.some((forn) => forn === this.idCD)
          );
        } else {
          this.formasDePagamento = forms;
        }

        this.formasCarregadas = true;

        if (!this.formasDePagamento.some((p) => p.id === this.forma.idFormaPag)) {
          this.forma.idFormaPag = undefined;
          this.forma.idCondPag = undefined;
        }

        this.carregandoService.esconde('buscaFormasDePagamento');

        if (this.forma.idFormaPag !== null && this.forma.idFormaPag !== undefined) {
          await this.buscarCondicoesDePagamento();
        }

        if (sincronizar) {
          this.sincronizarComListas();
        }
      },
      (erro) => {
        this.carregandoService.esconde('buscaFormasDePagamento');
        this.notificacaoService.erro(erro);
      }
    );
  }

  recarregarFormasDePagamento(): void {
    this.formasDePagamento = [];
    this.condicoesDePagamento = [];
    this.formasCarregadas = false;
    this.condicoesCarregadas = false;
    this.buscarFormasDePagamento(true);
  }

  /**
   * Depois da recarga, a forma passa a ter a taxa da condição na lista nova, ou zero se a condição foi retirada. Se a
   * taxa mudou, o pedido é recalculado.
   */
  private sincronizarComListas(): void {
    const taxaAnterior = this.forma.taxa;

    this.aplicarListas();

    if (Number(this.forma.taxa || 0) !== Number(taxaAnterior || 0)) {
      this.atualizar.emit(false);
    }
  }

  /**
   * Descrição e taxa da forma a partir das listas. Sem condição, a taxa é zero; com condição, só a lista carregada
   * diz a taxa, e até lá a forma mantém a que tem.
   */
  private aplicarListas(): void {
    if (this.formasCarregadas) {
      const formaPag = this.formasDePagamento.find((p) => p.id === this.forma.idFormaPag);

      this.forma.descricaoFormaPag = formaPag ? formaPag.descricao : '';
    }

    if (this.forma.idCondPag === null || this.forma.idCondPag === undefined) {
      this.forma.descricaoCondPag = '';
      this.forma.taxa = 0;
    } else if (this.condicoesCarregadas) {
      const condPag = this.condicoesDePagamento.find((p) => p.idCondPag === this.forma.idCondPag);

      this.forma.descricaoCondPag = condPag ? condPag.descricao : '';
      this.forma.taxa = condPag ? condPag.taxaDA : 0;
    }
  }

  async alterouFormaPagamento(): Promise<void> {
    const idFormaPag = this.forma.idFormaPag;
    this.forma.idCondPag = undefined;
    this.condicoesDePagamento = [];

    await this.buscarCondicoesDePagamento();

    if (this.forma.idFormaPag !== idFormaPag) {
      return;
    }

    await this.salvar();
  }

  async buscarCondicoesDePagamento(): Promise<void> {
    if (this.forma.idFormaPag !== null && this.forma.idFormaPag !== undefined) {
      const idFormaPag = this.forma.idFormaPag;
      this.condicoesCarregadas = false;
      this.carregandoService.mostra('buscaCondicoesDePagamento');

      try {
        const conds = await this.condicaoDePagamentoService.consultarPorForma(idFormaPag, 1).toPromise();

        // Ignora uma resposta atrasada caso outra forma tenha sido selecionada durante a consulta.
        if (this.forma.idFormaPag !== idFormaPag) {
          return;
        }

        this.condicoesDePagamento = conds.map((cond) => {
          const foraDoLimite = false; // this.condicaoDePagamentoService.verificaForaDoLimite(cond, this.forma.valor);
          return { ...cond, disabled: foraDoLimite };
        });

        this.condicoesCarregadas = true;

        const selecionado = this.condicoesDePagamento.find((c) => c.idCondPag == this.forma.idCondPag);

        if (selecionado === undefined || selecionado === null || selecionado.disabled) {
          this.forma.idCondPag = undefined;
        }
      } catch (erro) {
        this.notificacaoService.erro(erro);
      } finally {
        this.carregandoService.esconde('buscaCondicoesDePagamento');
      }
    } else {
      this.condicoesDePagamento = [];
      this.condicoesCarregadas = true;
      this.forma.idCondPag = undefined;
    }
  }

  async alterouCondicaoPagamento() {
    this.forma.valor = 0;
    this.forma.valor = await this.getValorRestante();

    await this.salvar();
  }

  async salvar(emitirAtualizacao = true): Promise<boolean> {
    if (this.somenteLeitura) {
      return true;
    }

    this.valorRestante = await this.getValorRestante();

    this.forma.valor = Number(this.forma.valor);
    this.forma.total = Number(this.forma.total);

    if (this.forma.total === null || this.forma.total === undefined || isNaN(this.forma.total)) {
      this.forma.total = 0;
    }

    // if (this.forma.valor > this.valorRestante) {
    //   return;
    // }

    const condPag = this.condicoesDePagamento.find((p) => p.idCondPag === this.forma.idCondPag);

    this.aplicarListas();

    if (condPag && (!emitirAtualizacao || (emitirAtualizacao && this.forma.tipo === TipoVendaFormaPag.Variavel))) {
      const foraDoLimite = this.condicaoDePagamentoService.verificaForaDoLimite(condPag, this.forma.valor);

      if (foraDoLimite) {
        swal({
          title: 'ATENÇÃO!!!',
          html: `A condição de pagamento não é válida para esse valor!!! <br/><strong>R$ ${this.forma.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
            ${condPag.valorMinimo !== 0 ? `<br/>Valor mínimo: <strong>R$ ${condPag.valorMinimo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>` : ``}
            ${condPag.valorMaximo !== 0 ? `<br/>Valor máximo: <strong>R$ ${condPag.valorMaximo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>` : ``}`,
          buttonsStyling: false,
          confirmButtonClass: 'btn btn-warning',
          type: 'warning'
        }).catch(swal.noop);

        this.forma.valor = undefined;
        this.forma.total = undefined;
        this.forma.taxa = 0;
        this.forma.valorTaxa = 0;
        this.forma.valorRateio = 0;
        this.forma.rateio = 0;
      }
    }

    const valido =
      // this.forma.idCondPag !== null && this.forma.idCondPag !== undefined &&
      this.forma.idFormaPag !== null &&
      this.forma.idFormaPag !== undefined &&
      this.forma.valor !== null &&
      this.forma.valor !== undefined &&
      this.forma.tipo !== null &&
      this.forma.tipo !== undefined;

    if (valido && emitirAtualizacao) {
      this.atualizar.emit(false);
    }

    // this.dialogRef.close(this.forma);
    return valido;
  }

  fechar() {
    // this.dialogRef.close();
  }

  excluirForma() {
    this.excluir.emit(this.forma);
  }

  formatarTotal() {
    if (
      !this.forma ||
      this.forma.total === null ||
      this.forma.total === undefined ||
      this.forma.idCondPag === null ||
      this.forma.idCondPag === undefined
    ) {
      return '';
    }

    const condPag = this.condicoesDePagamento.find((condicao) => condicao.idCondPag === this.forma.idCondPag);

    if (!condPag || !condPag.nParcelas) {
      return '';
    }

    const total = Number(this.forma.total);
    const parcelas = Number(condPag.nParcelas);
    const valorParcela = total / parcelas;

    return `${parcelas} x de R$ ${valorParcela.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | R$ ${total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}
